import React, {
  type ReactElement,
  type ReactNode,
  useCallback,
  useState,
  type FC,
  useMemo,
  type PropsWithChildren,
} from 'react';
import { FormattedMessage } from '@/components/formatted_message/FormattedMessage';
import { FormattedBytes } from '@/components/FormattedBytes';
import theme from '@/theme';
import type { UploadProps as AntdUploadProps, UploadFile as AntdUploadFile, UploadProps } from 'antd';
import { Upload } from 'antd';
import { OpacityLoader } from '@/components/Loaders/OpacityLoader';
import { useDispatch } from 'react-redux';
import { fileActions } from '@/containers/App/actions';
import type { UploadChangeParam, UploadFile } from 'antd/lib/upload';
import type { RcFile } from 'antd/lib/upload/interface';
import {
  ALLOWED_ACCEPT,
  ALLOWED_EXTENSIONS,
  MAX_FILE_SIZE,
  isFileTypeAllowed,
  type SerializedFile,
} from '@enmon/commons/lib/enmon';
import { useFiles } from '@/services/hooks/files/useFiles';
import { useDeleteFile } from '@/services/hooks/files/useDelete';
import { Popconfirm } from '@/components/popconfirm';
import type { StrictOmit } from '@enmon/commons';
import { DropzoneMessages as messages } from './messages';
import { StyledDropzone, StyledDropzoneListItem, UploadWrapper } from './components/StyledDropzone';

interface ExtendedUploadFile extends AntdUploadFile {
  isExisting?: boolean;
  originalFile?: SerializedFile;
}

export interface DropzoneProps extends Omit<AntdUploadProps, 'onChange'> {
  height?: number;
  isLoading?: boolean;
  isUploading?: boolean;
  /**
   * Callback invoked whenever the file list changes (files added or removed).
   * Returns the complete new array of files (excluding existing files from the backend).
   * Parent component should replace its file state with this array.
   */
  onChange?: (files: UploadFile[]) => void;
  /**
   * Arguments for useFiles hook, should contain filter for files that should be displayed in gallery.
   * When not provided files prop is used instead.
   */
  filesParameters?: Parameters<typeof useFiles>;
  /**
   * Callback to be called after successful deletion of existing file.
   */
  onDeleteExistingFile?: (fileId: string) => void;
  /**
   * Custom content rendered inside the drop area instead of the default prompt.
   */
  placeholder?: ReactNode;
}

export const Dropzone: FC<StrictOmit<DropzoneProps, 'listType'>> = props => {
  const {
    isLoading: isLoadingParent,
    isUploading: isUploadingParent,
    accept: acceptedTypes,
    fileList: controlledFileList,
    onChange: controlledOnChange,
    maxCount,
    showUploadList,
    disabled,
    filesParameters,
    onDeleteExistingFile,
    placeholder,
    ...restProps
  } = props;

  const resolvedAccept = useMemo(() => {
    if (!acceptedTypes) return ALLOWED_ACCEPT;
    const types = typeof acceptedTypes === 'string' ? acceptedTypes : acceptedTypes?.format;
    if (!types) return ALLOWED_ACCEPT;

    const items = types
      .split(',')
      .map(t => t.trim())
      .filter(Boolean);
    const allowed = items.filter(item => {
      if (item.startsWith('.')) {
        return (ALLOWED_EXTENSIONS as readonly string[]).includes(item.slice(1));
      }
      return true;
    });
    return allowed.length > 0 ? allowed.join(',') : ALLOWED_ACCEPT;
  }, [acceptedTypes]);

  const dispatch = useDispatch();
  const [uncontrolledFileList, setUncontrolledFileList] = useState<AntdUploadFile[]>([]);
  const [fileToDelete, setFileToDelete] = useState<ExtendedUploadFile | null>(null);

  // Fetch existing files if filesFilter is provided
  const { data: filesResponse, isFetching: isLoadingExistingFiles } = useFiles(...(filesParameters ?? []));
  const { mutate: deleteFile, isLoading: isDeletingFile } = useDeleteFile();
  const isLoading = isLoadingParent || isUploadingParent || isDeletingFile || isLoadingExistingFiles;

  // Transform existing files to Upload file format
  const existingFiles = useMemo<ExtendedUploadFile[]>(() => {
    if (!filesResponse?.result) return [];

    return filesResponse.result.map((file: SerializedFile) => ({
      uid: file._id,
      name: file.name,
      status: 'done',
      url: file.signedUrl,
      thumbUrl: file.thumbnail?.signedUrl,
      isExisting: true,
      originalFile: file,
    }));
  }, [filesResponse]);

  const isControlled = controlledFileList !== undefined;
  const newFileList = isControlled ? controlledFileList : uncontrolledFileList;

  // Merge existing files with new files (existing files first)
  const currentFileList = useMemo<ExtendedUploadFile[]>(
    () => [...newFileList, ...existingFiles],
    [existingFiles, newFileList],
  );

  const handleBeforeUpload = (file: RcFile): boolean | string | Promise<File | void> => {
    if (maxCount && currentFileList.length >= maxCount) {
      dispatch(fileActions.fileLimit.error());
      return Upload.LIST_IGNORE;
    }

    if (file.size > MAX_FILE_SIZE) {
      dispatch(fileActions.fileLimit.error());
      return Upload.LIST_IGNORE;
    }

    const acceptedTypesString = typeof acceptedTypes === 'string' ? acceptedTypes : acceptedTypes?.format;

    const parsedAcceptedTypes = acceptedTypesString
      ?.toLowerCase()
      .split(',')
      .map(t => t.trim());

    // Check if file passes basic validation
    if (!(file.name && isFileTypeAllowed(file.name))) {
      dispatch(fileActions.uploadUnsupportedFile());
      return Upload.LIST_IGNORE;
    }

    // If no accept types specified, allow the file
    if (!parsedAcceptedTypes) {
      return true;
    }

    // Check against accepted types (supports both extensions like .pdf and MIME types like application/pdf)
    const fileExtension = file.name.toLowerCase().split('.').pop();
    const isAccepted = parsedAcceptedTypes.some(acceptedType => {
      // If accepted type starts with dot, it's a file extension
      if (acceptedType.startsWith('.')) {
        return acceptedType === `.${fileExtension ?? ''}`;
      }
      // Otherwise, it's a MIME type
      return acceptedType === file.type.toLowerCase();
    });

    if (isAccepted) {
      return true;
    }

    dispatch(fileActions.uploadUnsupportedFile());
    return Upload.LIST_IGNORE;
  };

  const itemRender = useCallback(
    (originNode: ReactElement, file: AntdUploadFile): ReactNode => {
      const fileIsUploading =
        file.status === 'uploading' || (isUploadingParent && file.status !== 'done' && file.status !== 'error');

      const extendedFile = file as ExtendedUploadFile;
      const fileSize = extendedFile.size ?? extendedFile.originalFile?.size;

      const children = React.Children.toArray((originNode.props as PropsWithChildren).children);
      if (fileSize) {
        children.splice(
          2,
          0,
          <span key="file-size" style={{ marginLeft: '8px', color: '#888', fontSize: '12px' }}>
            (<FormattedBytes decimals={2} value={fileSize} />)
          </span>,
        );
      }

      const clonedNode = React.cloneElement(originNode, { ...(originNode.props as PropsWithChildren), children });

      return (
        <StyledDropzoneListItem>
          {clonedNode}
          {fileIsUploading && <OpacityLoader />}
        </StyledDropzoneListItem>
      );
    },
    [isUploadingParent],
  );

  const handleRemove = (fileToRemove: AntdUploadFile) => {
    const extendedFile = fileToRemove as ExtendedUploadFile;

    // For existing files, show confirmation (handled by Popconfirm in itemRender)
    if (extendedFile.isExisting) {
      setFileToDelete(extendedFile);
      return; // Prevent immediate removal, wait for confirmation
    }

    // For new files, remove immediately and notify parent
    const updatedFileList = newFileList.filter(f => f.uid !== fileToRemove.uid);
    setUncontrolledFileList(updatedFileList);
    controlledOnChange?.(updatedFileList);
  };

  const handleConfirmDelete = () => {
    if (!fileToDelete?.originalFile) return;

    deleteFile(fileToDelete.originalFile._id, {
      onSuccess: () => {
        if (fileToDelete.originalFile) {
          onDeleteExistingFile?.(fileToDelete.originalFile._id);
        }
        setFileToDelete(null);
      },
      onError: () => {
        setFileToDelete(null);
      },
    });
  };

  const handleCustomRequest: UploadProps['customRequest'] = options => {
    const { onSuccess, onError } = options;

    if (onSuccess) {
      // Just mark as successful - Ant Design will trigger onChange with updated fileList
      onSuccess({});
    } else if (onError) {
      onError(new Error('Local file processing failed.'));
    }
  };

  const handleAntdChange = (info: UploadChangeParam<AntdUploadFile>) => {
    const filteredFileList = info.fileList.filter(f => !(f as ExtendedUploadFile).isExisting);

    setUncontrolledFileList(filteredFileList);
    controlledOnChange?.(filteredFileList);
  };

  return (
    <Popconfirm
      okButtonProps={{ loading: isDeletingFile, danger: true }}
      open={!!fileToDelete}
      placement="bottom"
      title={<FormattedMessage {...messages.deleteConfirmation} />}
      onCancel={() => setFileToDelete(null)}
      onConfirm={handleConfirmDelete}
    >
      <UploadWrapper>
        <StyledDropzone
          {...restProps}
          accept={resolvedAccept}
          beforeUpload={handleBeforeUpload}
          customRequest={handleCustomRequest}
          disabled={disabled || isLoading}
          fileList={currentFileList}
          itemRender={itemRender}
          maxCount={maxCount}
          showUploadList={showUploadList}
          onChange={handleAntdChange}
          onRemove={handleRemove}
        >
          {isUploadingParent ? (
            <FormattedMessage {...messages.uploadingFiles} />
          ) : (
            (placeholder ?? (
              <FormattedMessage
                {...messages.title}
                values={{
                  click: (
                    <span style={{ color: theme.color.semantic.link.default }}>
                      <FormattedMessage {...messages.byClick} />
                    </span>
                  ),
                }}
              />
            ))
          )}
        </StyledDropzone>
        {isLoading && <OpacityLoader />}
      </UploadWrapper>
    </Popconfirm>
  );
};
